import Cookies from 'js-cookie';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { ILocation } from '@/interface/ILocation';
import type { IMarketPageEventCount } from '@/interface/IMarketPageEventCount';
import { BrandType, MarketPage, SportType } from '@/interface/enum';
import useState from '@/libraries/composables/useState';
import { getSportTypeKey } from '@/libraries/helpers/sportsHelper';
import { getHost } from '@/libraries/url';
import { keyLookup } from '@/libraries/utils/utils';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

interface IFilter {
    type: string;
    option: IFilterOption;
}

interface IFilterOption {
    leagueIds: number[];
    startDateTime: Date | null;
    endDateTime: Date | null;
    dateFilterIndex: number;
}

interface ITopScreenToast {
    status: 'success' | 'error' | 'warning' | 'placeBetSuccess';
    messageKey: string;
    messageData?: any;
    messagePosition?: 'position-top' | 'position-bottom';
    callBack?: () => void;
    duration?: number;
    timeoutId?: number;
}

const defaultTopScreenToast: ITopScreenToast = {
    status: 'success',
    messageKey: '',
    messageData: {},
    messagePosition: undefined,
    timeoutId: 0,
    callBack: undefined,
};

export const useRootStore = defineStore('root', () => {
    const [isAsi, setIsAsi] = useState(__ENV_ASI__);
    const [brand, setBrand] = useState<BrandType>(BrandType.Unknown);
    const [isMainDomain, setIsMainDomain] = useState(false);
    const [isSportsSite, setIsSportsSite] = useState(false);
    const sportType = ref<SportType>(Number(localStorage.getItem('sportType')) || SportType.Unknown);
    const marketPage = ref<MarketPage>(MarketPage.Live);
    const topScreenToast = ref<ITopScreenToast>(defaultTopScreenToast);
    const [isExpandAll, setIsExpandAll] = useState(true);
    const isMixParlayMode = ref<boolean>(localStorage.getItem('isMixParlayMode') === 'true');
    const showAcceptAnyOddsBanner = ref<boolean | null>(JSON.parse(window.localStorage.getItem('aaoBanner') ?? 'null'));
    const showAacovPopup = ref<boolean | null>(JSON.parse(window.localStorage.getItem('aacovPopup') ?? 'null')); // null = never see popup yet, true = show, false = don't show
    const hasSeenCashOutTutorial = ref(!!Cookies.get('CashoutTutorial'));
    const [failedPlaceBetShowAaoBanner, setFailedPlaceBetShowAaoBanner] = useState(false);
    const _filter = ref<IFilter[]>([]);
    const [oddsPosition, setOddsPosition] = useState(0);
    const [marketPageEventCount, setMarketPageEventCount] = useState<IMarketPageEventCount>({});
    const [isWsConnected, setIsWsConnected] = useState(false);
    const [countryCode, setCountryCode] = useState('');
    const isMatchDetailPage = ref(false);
    const [isWidgetOpened, setIsWidgetOpened] = useState(false);
    const isCashOutTooltipShowOnce = ref((Number(Cookies.get('isCashOutTooltipShowOnce')) || 0) === 1);

    const filter = computed(() => {
        const type = `${isMixParlayMode.value ? 'm' : 's'}${marketPage.value}`;
        return _filter.value.find(item => item.type === type)?.option;
    });
    const hasFilter = computed(() => {
        if (!filter.value) return false;
        return (filter.value.leagueIds && (filter.value.leagueIds.length > 0))
            || filter.value.startDateTime
            || filter.value.endDateTime;
    });

    function getDefaultMarketPage(): MarketPage {
        const { isIndiaSpecialRule } = storeToRefs(useFeatureToggleStore());
        return isIndiaSpecialRule.value ? MarketPage.Today : MarketPage.Live;
    }

    function isValidSportType(sportType: SportType) {
        return sportType !== SportType.Unknown && getSportTypeKey(sportType);
    }

    function getTargetSportType(_sportType: SportType): SportType {
        const { defaultSportType } = storeToRefs(useFeatureToggleStore());

        if (isValidSportType(_sportType)) return _sportType;
        if (!isAsi.value && isValidSportType(sportType.value)) {
            return sportType.value;
        }

        return defaultSportType.value;
    }

    function updateSportType(_sportType: SportType = SportType.Unknown, changeMarketPage = true) {
        const targetSportType = getTargetSportType(_sportType);

        if (changeMarketPage) {
            marketPage.value = getDefaultMarketPage();
        }
        isExpandAll.value = true;
        sportType.value = targetSportType;
        localStorage.setItem('sportType', targetSportType.toString());
    }

    function updateMarketPage(_marketPage: MarketPage = MarketPage.Unknown) {
        marketPage.value = _marketPage !== MarketPage.Unknown && keyLookup(MarketPage, _marketPage)
            ? _marketPage
            : getDefaultMarketPage();
        isExpandAll.value = true;
    }

    function _setTopScreenToast({
        status, messageKey, messageData, messagePosition, callBack, timeoutId,
    }: ITopScreenToast) {
        topScreenToast.value = {
            ...defaultTopScreenToast,
            status,
            messageKey,
            messageData,
            messagePosition,
            callBack,
            timeoutId,
        };
    }

    function setIsMixParlayMode(_isMixParlayMode: boolean, safeCall = false) {
        if (!safeCall) {
            console.warn('Please use \'handleSetIsMixParlayMode\' instead of \'setIsMixParlayMode\'!');
        }
        isMixParlayMode.value = _isMixParlayMode;
        localStorage.setItem('isMixParlayMode', _isMixParlayMode.toString());
        isExpandAll.value = true;
    }

    function setShowAcceptAnyOddsBanner(showBanner: boolean) {
        if (showBanner && showAcceptAnyOddsBanner.value === false) { // if already false, means user already seen before and closed banner
            return;
        }
        showAcceptAnyOddsBanner.value = showBanner;
        window.localStorage.setItem('aaoBanner', showBanner.toString());
    }

    function setShowAcceptAnyCashOutValuePopup(showPopup: boolean) {
        if (showPopup && showAacovPopup.value === false) { // if already false, means user already seen before and closed popup
            return;
        }
        showAacovPopup.value = showPopup;
        window.localStorage.setItem('aacovPopup', showPopup.toString());
    }

    function setHasSeenCashOutTutorial(_hasSeenCashOutTutorial: boolean) {
        hasSeenCashOutTutorial.value = _hasSeenCashOutTutorial;
        Cookies.set('CashoutTutorial', _hasSeenCashOutTutorial.toString(), { domain: getHost() });
    }

    function setFilter(option: IFilterOption) {
        const type = `${isMixParlayMode.value ? 'm' : 's'}${marketPage.value}`;
        _filter.value = [..._filter.value.filter(item => item.type !== type), { type, option }];
    }

    function clearFilter() {
        _filter.value = [];
    }

    function setIsCashOutTooltipShowOnce(_isCashOutTooltipShowOnce: boolean) {
        if (!isCashOutTooltipShowOnce.value) {
            Cookies.set('isCashOutTooltipShowOnce', _isCashOutTooltipShowOnce ? '1' : '0', { domain: getHost(), expires: 1 });
        }
        isCashOutTooltipShowOnce.value = _isCashOutTooltipShowOnce;
    }

    function handleSetIsMixParlayMode(_isMixParlayMode: boolean) {
        setIsMixParlayMode(_isMixParlayMode, true);
    }

    function setTopScreenToast({
        status, messageKey, messageData, messagePosition = undefined, duration = 5000, callBack,
    }: ITopScreenToast) {
        clearTimeout(topScreenToast.value.timeoutId);
        const timeoutId = window.setTimeout(() => {
            clearTopScreenToast();
        }, duration);

        _setTopScreenToast({
            status,
            messageKey,
            messageData,
            messagePosition,
            callBack,
            timeoutId,
        });
    }

    function clearTopScreenToast() {
        clearTimeout(topScreenToast.value.timeoutId);
        const callBack = topScreenToast.value.callBack;
        if (typeof callBack === 'function') callBack();

        _setTopScreenToast(defaultTopScreenToast);
    }

    function showErrorToast(messageKey: string) {
        setTopScreenToast({
            status: 'error',
            messageKey,
        });
    }

    const isSboTop = computed(() => brand.value === BrandType.Sbotop);
    const brandName = computed(() => BrandType[brand.value].toUpperCase());

    const isSoccer = computed(() => sportType.value === SportType.Soccer);

    function setLocation(locationResponse: ILocation) {
        const { countryCode, brand, isMainDomain } = locationResponse;
        setCountryCode(countryCode);
        setBrand(brand);
        setIsMainDomain(isMainDomain);
    }

    return {
        isAsi,
        brand,
        isMainDomain,
        isSportsSite,
        isSboTop,
        brandName,
        sportType,
        marketPage,

        topScreenToast,
        isExpandAll,
        isMixParlayMode,
        showAcceptAnyOddsBanner,
        showAacovPopup,
        hasSeenCashOutTutorial,
        failedPlaceBetShowAaoBanner,
        oddsPosition,
        marketPageEventCount,
        isWsConnected,
        countryCode,
        isMatchDetailPage,
        isWidgetOpened,
        isCashOutTooltipShowOnce,

        filter,
        hasFilter,
        isSoccer,

        setLocation,
        setIsAsi,
        setBrand,
        setIsMainDomain,
        setIsSportsSite,
        updateSportType,
        updateMarketPage,
        setIsExpandAll,
        setShowAcceptAnyOddsBanner,
        setShowAcceptAnyCashOutValuePopup,
        setHasSeenCashOutTutorial,
        setFailedPlaceBetShowAaoBanner,
        setFilter,
        clearFilter,
        setOddsPosition,
        setMarketPageEventCount,
        setIsWsConnected,
        setCountryCode,
        setIsWidgetOpened,
        setIsCashOutTooltipShowOnce,
        handleSetIsMixParlayMode,
        setTopScreenToast,
        clearTopScreenToast,
        showErrorToast,
    };
});
